// src/component/OTP/otp.jsx

import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import Image from "../../asserts/signup.png";
import { NavLink, useNavigate } from "react-router-dom";

import { MuiOtpInput } from "mui-one-time-password-input";
import { axiosInstance } from "../Api/instance";
import { PrimaryButton } from "../ui/Buttons";
import { styled } from "@mui/material/styles";

const PageContainer = styled(Grid)(({ theme }) => ({
  minHeight: "100vh",
  backgroundColor: theme.palette.background.default,
  overflow: "hidden",
}));

const FormContainer = styled(Grid)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: "48px 40px",
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.down("sm")]: {
    padding: "32px 20px",
  },
}));

const ImageContainer = styled(Grid)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1F2937' : '#F8FAFC',
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

const SigninPage = () => {
  const [otp, setOtp] = useState("");
  const [email, setemail] = useState("");
  const [loadingOtp, setLoadingOtp] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const Navigate = useNavigate();

  // Snackbar Toast notification
  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });

  const showToast = (message, severity = "info") => {
    setToast({ open: true, message, severity });
  };

  const handleChange = (newValue) => {
    setOtp(newValue);
  };

  // Preserve existing logic writing forget key on every render cycle
  localStorage.setItem("forget", email);

  const handleClickOpen = async () => {
    if (!email) {
      showToast("Please enter your email first", "warning");
      return;
    }
    setLoadingOtp(true);
    try {
      const response = await axiosInstance.post(`put/U_updateOTP.php`, {
        email: email,
      });

      if (response.data.message === "success") {
        showToast("OTP Sent successfully.", "success");
      } else {
        showToast("Email address is invalid", "error");
      }
    } catch (error) {
      console.error("Error check email or password:", error);
      showToast("Enter Valid Credentials", "error");
    } finally {
      setLoadingOtp(false);
    }
  };

  const handleOTP = async () => {
    if (!otp || otp.length < 4) {
      showToast("Please enter a valid OTP", "warning");
      return;
    }
    setLoadingSubmit(true);
    try {
      const response = await axiosInstance.post(
        `post/U_ValidateOtp_Token.php`,
        {
          email: email,
          otp: otp,
        }
      );

      console.log("Success:", response.data);
      if (response.data.message === "Success") {
        if (response.data.message === "timeout") {
          Navigate("/signin");
          return;
        }
        Navigate("/forget");
      } else {
        showToast("Invalid OTP code", "error");
        console.error("Error in response:", response.data);
      }
    } catch (error) {
      console.error("Error check email or password:", error);
      showToast("Verification failed. Please try again.", "error");
    } finally {
      setLoadingSubmit(false);
    }
  };

  useEffect(() => {
    localStorage.clear();
  }, []);

  return (
    <PageContainer container>
      <Snackbar 
        open={toast.open} 
        autoHideDuration={4000} 
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert 
          onClose={() => setToast(prev => ({ ...prev, open: false }))} 
          severity={toast.severity} 
          sx={{ width: "100%", borderRadius: "12px", boxShadow: 3 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Form Left Panel */}
      <FormContainer item xs={12} sm={6}>
        <Box style={{ maxWidth: "420px", width: "100%", margin: "0 auto" }}>
          <Typography variant="h2" style={{ fontWeight: 800, color: "var(--color-primary)", marginBottom: "8px" }}>
            Verification
          </Typography>
          <Typography variant="body1" color="textSecondary" style={{ marginBottom: "32px" }}>
            Verify your email address to recover your password.
          </Typography>

          <form onSubmit={(e) => { e.preventDefault(); }}>
            <Box style={{ marginBottom: "20px" }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                Email Address
                <EmailIcon fontSize="inherit" color="action" />
              </Typography>
              <TextField
                variant="outlined"
                fullWidth
                id="email"
                placeholder="nursing@example.com"
                value={email}
                onChange={(e) => setemail(e.target.value)}
                required
              />
            </Box>

            <PrimaryButton
              type="button"
              loading={loadingOtp}
              onClick={handleClickOpen}
              style={{ width: "100%", height: "46px" }}
            >
              Get OTP
            </PrimaryButton>

            <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "32px" }}>
              <Typography variant="body1" style={{ fontWeight: 500, marginBottom: "16px" }}>
                Enter OTP Code
              </Typography>
              
              <MuiOtpInput
                value={otp}
                onChange={handleChange}
                style={{
                  width: "280px",
                  display: "flex",
                  justifyContent: "center",
                }}
              />
              
              <PrimaryButton
                type="button"
                loading={loadingSubmit}
                onClick={handleOTP}
                style={{ width: "100%", height: "46px", marginTop: "24px" }}
              >
                Submit
              </PrimaryButton>

              <Box style={{ textAlign: "center", marginTop: "24px" }}>
                <NavLink to="/signin" style={{ textDecoration: "none", color: "var(--color-primary)", fontWeight: 700, fontSize: "0.875rem" }}>
                  Back to Sign In
                </NavLink>
              </Box>
            </Box>
          </form>
        </Box>
      </FormContainer>

      {/* Decorative Right Panel */}
      <ImageContainer item xs={12} sm={6}>
        <img
          src={Image}
          alt="OTP Verification"
          style={{
            width: "90%",
            maxHeight: "80vh",
            objectFit: "contain",
            borderRadius: "20px",
          }}
        />
      </ImageContainer>
    </PageContainer>
  );
};

export default SigninPage;
